import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import type { MatchScore as MatchScoreType } from '../types';

interface MatchScoreProps {
  score: MatchScoreType;
}

const AnimatedNumber = ({ value, color }: { value: number; color: string }) => {
  const [displayed, setDisplayed] = useState(0);

  useEffect(() => {
    const duration = 800;
    const steps = 30;
    const increment = value / steps;
    let current = 0;
    const timer = setInterval(() => {
      current += increment;
      if (current >= value) {
        setDisplayed(value);
        clearInterval(timer);
      } else {
        setDisplayed(Math.round(current));
      }
    }, duration / steps);
    return () => clearInterval(timer);
  }, [value]);

  return (
    <span className="text-4xl font-bold leading-none" style={{ color }}>
      {displayed}
    </span>
  );
};

const MatchScore: React.FC<MatchScoreProps> = ({ score }) => {
  const getScoreColor = (val: number) => {
    if (val >= 75) return '#10b981';
    if (val >= 50) return '#f59e0b';
    return '#ef4444';
  };

  const getGrade = (val: number) => {
    if (val >= 90) return 'A';
    if (val >= 75) return 'B';
    if (val >= 60) return 'C';
    if (val >= 40) return 'D';
    return 'F';
  };

  return (
    <div className="flex flex-col gap-8">
      <motion.div
        className="text-center p-8 bg-subtle rounded-card"
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.3, ease: 'easeOut' }}
      >
        <div className="w-[120px] h-[120px] rounded-full border-[6px] flex flex-col items-center justify-center mx-auto mb-4" style={{ borderColor: getScoreColor(score.overallScore) }}>
          <AnimatedNumber value={score.overallScore} color={getScoreColor(score.overallScore)} />
          <span className="text-base font-semibold text-secondary-foreground">{getGrade(score.overallScore)}</span>
        </div>
        <p className="text-base text-secondary-foreground max-w-[500px] mx-auto">{score.verdict}</p>
      </motion.div>

      <div>
        <h3 className="text-base font-semibold mb-4 text-foreground">Score Breakdown</h3>
        {[
          { label: 'Skills Match', value: score.breakdown.skillsMatch },
          { label: 'Experience Match', value: score.breakdown.experienceMatch },
          { label: 'Keyword Coverage', value: score.breakdown.keywordCoverage },
          { label: 'Seniority Alignment', value: score.breakdown.seniorityAlignment },
        ].map(({ label, value }, i) => (
          <motion.div
            key={label}
            className="flex items-center gap-4 mb-3"
            initial={{ opacity: 0, x: -10 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.2 + i * 0.1, duration: 0.3 }}
          >
            <span className="text-sm text-secondary-foreground min-w-[140px] max-md:min-w-[100px] max-md:text-xs">{label}</span>
            <div className="flex-1 h-2 bg-muted rounded-full overflow-hidden">
              <motion.div
                className="h-full rounded-full transition-all duration-500"
                initial={{ width: 0 }}
                animate={{ width: `${value}%` }}
                transition={{ delay: 0.3 + i * 0.1, duration: 0.6, ease: 'easeOut' }}
                style={{ backgroundColor: getScoreColor(value) }}
              />
            </div>
            <span className="text-sm font-semibold text-foreground min-w-[40px] text-right">{value}%</span>
          </motion.div>
        ))}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <motion.div
          className=""
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5, duration: 0.3 }}
        >
          <h3 className="text-sm font-semibold mb-3 text-foreground">✅ Strengths</h3>
          <ul className="list-none p-0 m-0 flex flex-col gap-2">
            {score.strengths.map((s, i) => (
              <li key={i} className="text-xs text-secondary-foreground p-2 px-3 bg-subtle rounded-btn leading-relaxed border-l-3 border-l-emerald-500">{s}</li>
            ))}
          </ul>
        </motion.div>

        <motion.div
          className=""
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6, duration: 0.3 }}
        >
          <h3 className="text-sm font-semibold mb-3 text-foreground">⚠️ Gaps</h3>
          <ul className="list-none p-0 m-0 flex flex-col gap-2">
            {score.gaps.map((g, i) => (
              <li key={i} className="text-xs text-secondary-foreground p-2 px-3 bg-subtle rounded-btn leading-relaxed border-l-3 border-l-amber-500">{g}</li>
            ))}
          </ul>
        </motion.div>

        <motion.div
          className=""
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.7, duration: 0.3 }}
        >
          <h3 className="text-sm font-semibold mb-3 text-foreground">💡 Recommendations</h3>
          <ul className="list-none p-0 m-0 flex flex-col gap-2">
            {score.recommendations.map((r, i) => (
              <li key={i} className="text-xs text-secondary-foreground p-2 px-3 bg-subtle rounded-btn leading-relaxed border-l-3 border-l-blue-500">{r}</li>
            ))}
          </ul>
        </motion.div>

        <motion.div
          className=""
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.8, duration: 0.3 }}
        >
          <h3 className="text-sm font-semibold mb-3 text-foreground">🎤 Interview Talking Points</h3>
          <ul className="list-none p-0 m-0 flex flex-col gap-2">
            {score.interviewTalkingPoints.map((t, i) => (
              <li key={i} className="text-xs text-secondary-foreground p-2 px-3 bg-subtle rounded-btn leading-relaxed border-l-3 border-l-violet-500">{t}</li>
            ))}
          </ul>
        </motion.div>
      </div>
    </div>
  );
};

export default MatchScore;
